import { Checkbox, type StackProps, XStack } from 'smoothie';

import {
    BankingPolicyDisclosures,
    CombinedBankingPolicyDisclosures,
    FRUITFUL_BATCH_OPEN_APEX_ACCOUNTS_POLICY_DISCLOSURES,
    FRUITFUL_BATCH_OPEN_CARD_POLICY_DISCLOSURES,
    FRUITFUL_BATCH_OPEN_CASH_POLICY_DISCLOSURES,
    FRUITFUL_CARD_ACCOUNT_OPEN_POLICY_DISCLOSURES,
    FRUITFUL_CASH_ACCOUNT_OPEN_POLICY_DISCLOSURES,
} from './Disclosures';

const TermsCheckbox = ({
    id,
    isChecked,
    onPress,
    size = 'large',
}: {
    id: string;
    isChecked: boolean;
    onPress: () => void;
    size?: 'default' | 'large';
}) => {
    return (
        <Checkbox
            onPress={onPress}
            checked={isChecked}
            id={id}
            size={size}
            tone="positive"
        />
    );
};

interface CardTermsProps extends StackProps {
    termsAccepted: boolean;
    onPress: () => void;
    autoPayEnabled: boolean;
}

export const CardTerms = ({
    termsAccepted,
    onPress,
    autoPayEnabled,
    ...props
}: CardTermsProps) => {
    return (
        <XStack gap={8} userSelect="none" {...props}>
            <TermsCheckbox
                id="cardTermsCheckbox"
                isChecked={termsAccepted}
                onPress={onPress}
            />
            <BankingPolicyDisclosures
                onPress={onPress}
                acknowledgementText="I acknowledge I have read and agreed to Fruitful's"
                policies={FRUITFUL_CARD_ACCOUNT_OPEN_POLICY_DISCLOSURES}
            />
        </XStack>
    );
};

interface BankingTermsProps extends StackProps {
    termsAccepted: boolean;
    onPress: () => void;
}

const BankingTerms = ({
    termsAccepted,
    onPress,
    ...props
}: BankingTermsProps) => {
    return (
        <XStack gap={8} userSelect="none" {...props}>
            <TermsCheckbox
                id="bankingTermsCheckbox"
                isChecked={termsAccepted}
                onPress={onPress}
            />
            <BankingPolicyDisclosures
                onPress={onPress}
                acknowledgementText="I acknowledge I have read and agreed to Fruitful's"
                policies={FRUITFUL_CASH_ACCOUNT_OPEN_POLICY_DISCLOSURES}
            />
        </XStack>
    );
};

export const BatchOpenCashBankingTerms = ({
    termsAccepted,
    onPress,
    ...props
}: BankingTermsProps) => {
    return (
        <XStack gap={8} userSelect="none" {...props}>
            <TermsCheckbox
                id="batchOpenCashTermsCheckbox"
                isChecked={termsAccepted}
                onPress={onPress}
                size="default"
            />
            <CombinedBankingPolicyDisclosures
                onPress={onPress}
                acknowledgementText="I acknowledge that I have read and agreed to"
                policies={FRUITFUL_BATCH_OPEN_CASH_POLICY_DISCLOSURES}
                textSize="small"
            />
        </XStack>
    );
};

export const BatchOpenCardBankingTerms = ({
    termsAccepted,
    onPress,
    ...props
}: BankingTermsProps) => {
    return (
        <XStack gap={8} userSelect="none" {...props}>
            <TermsCheckbox
                id="batchOpenCardTermsCheckbox"
                isChecked={termsAccepted}
                onPress={onPress}
                size="default"
            />
            <CombinedBankingPolicyDisclosures
                onPress={onPress}
                acknowledgementText="I acknowledge that I have read and agreed to"
                policies={FRUITFUL_BATCH_OPEN_CARD_POLICY_DISCLOSURES}
                textSize="small"
            />
        </XStack>
    );
};

export const BatchOpenApexAccountsBankingTerms = ({
    termsAccepted,
    onPress,
    acknowledgementText,
    ...props
}: BankingTermsProps & { acknowledgementText: string }) => {
    return (
        <XStack gap={8} userSelect="none" {...props}>
            <TermsCheckbox
                id="batchOpenApexAccountsTermsCheckbox"
                isChecked={termsAccepted}
                onPress={onPress}
                size="default"
            />
            <CombinedBankingPolicyDisclosures
                onPress={onPress}
                acknowledgementText={acknowledgementText}
                policies={FRUITFUL_BATCH_OPEN_APEX_ACCOUNTS_POLICY_DISCLOSURES}
                textSize="small"
            />
        </XStack>
    );
};

export default BankingTerms;
